import { useTranslation } from 'react-i18next';
import { ReactComponent as FileUploadIcon } from 'assets/icons/Screp.svg';
import ButtonAddPlus from 'components/ButtonAddPlus/ButtonAddPlus';
import EmptyList from 'components/EmptyList/EmptyList';
import StatusTaskButton from 'components/StatusTaskButton/StatusTaskButton';
import { TASK_TYPE, TYPE_RELATE_TO } from 'constants/general';
import formatFileUrl from 'utils/formatFileUrl';
import formattedData from 'utils/formattedData';

import type { TTaskStatus } from 'constants/interfaces';
import type ISingleTask from 'store/slices/tasksManagement/interfaces/ISingleTask';
import type ITaskInfoDetails from './interfaces/ITaskInfoDetails';

import styles from './sass/TaskInfoDetails.module.scss';

function TaskInfoDetails({
  data,
  onStatusChange,
  getPriorityIcon,
  getFileNameWithExtension,
  attachments,
  accessChangeStatus,
  onOpenCreateTaskModal,
}: ITaskInfoDetails) {
  const { t } = useTranslation();

  const handleGetCurrentRelatedTo = (dataItem: ISingleTask) => {
    let relatedTo = '';
    if (dataItem.type === TASK_TYPE.VCM) {
      relatedTo = dataItem.valueChainMap
        ? `${TASK_TYPE.VALUE_CHAIN} - ${dataItem.valueChainMap.name}`
        : TYPE_RELATE_TO.VALUE_CHAIN;
    } else if (dataItem.type === TASK_TYPE.MA) {
      relatedTo = dataItem.materialityAssessment
        ? `${TASK_TYPE.MATERIALITY_ASSESSMENT} - ${dataItem.materialityAssessment.name}`
        : TYPE_RELATE_TO.MATERIALITY_ASSESSMENT;
    }

    return relatedTo;
  };

  return (
    <div className={styles.info}>
      <div className={styles.infoItem}>
        <span className={styles.label}>
          {t('taskManagement.fields.relatedTo')}
          :
        </span>
        <span className={styles.value}>{data ? handleGetCurrentRelatedTo(data) : '-'}</span>
      </div>
      <div className={`${styles.infoItem} ${styles.infoItemUsers}`}>
        <span className={styles.label}>
          {t('taskManagement.fields.assignedTo')}
          :
        </span>
        <div className={styles.value}>
          {data?.assignedUsers && data?.assignedUsers.length > 0 ? data?.assignedUsers?.map((user) => (
            <div
              key={[user.id, user.firstName, user.lastName].join('_')}
              className={styles.assignedUser}
            >
              <div className={styles.userName}>
                {user?.firstName}
                {' '}
                {user?.lastName}
              </div>
              {user?.email ? <div className={styles.userEmail}>{user.email}</div> : null}
            </div>
          )) : '-'}
        </div>
      </div>

      <div className={styles.infoItem}>
        <span className={styles.label}>
          {t('taskManagement.fields.status')}
          :
        </span>
        <div className={styles.value}>
          <StatusTaskButton
            taskId={1}
            currentStatus={data?.status as TTaskStatus}
            onStatusChange={onStatusChange}
            disabled={!accessChangeStatus}
          />
        </div>
      </div>

      <div className={styles.infoItem}>
        <span className={styles.label}>
          {t('taskManagement.fields.createdDate')}
          :
        </span>
        <span className={styles.value}>{formattedData(data?.createdAt || '-')}</span>
      </div>

      <div className={styles.infoItem}>
        <span className={styles.label}>
          {t('taskManagement.fields.startDate')}
          :
        </span>
        <span className={styles.value}>{data?.startDate ? formattedData(data?.startDate) : '-'}</span>
      </div>

      <div className={styles.infoItem}>
        <span className={styles.label}>
          {t('taskManagement.fields.endDate')}
          :
        </span>
        <span className={styles.value}>{data?.endDate ? formattedData(data?.endDate) : '-'}</span>
      </div>
      <div className={styles.infoItem}>
        <span className={styles.label}>
          {t('taskManagement.fields.closedDate')}
          :
        </span>
        <span className={styles.value}>{data?.closedAt ? formattedData(data?.closedAt) : '-'}</span>
      </div>
      <div className={styles.infoItem}>
        <span className={styles.label}>
          {t('taskManagement.fields.priority')}
          :
        </span>
        <div className={styles.value}>
          <div className={styles.priorityBadge}>
            <span className={styles.priorityIcon}>
              {getPriorityIcon(data?.priority || '')}
            </span>
            {data?.priority}
          </div>
        </div>
      </div>
      <div className={`${styles.infoItem} ${styles.infoItemUsers}`}>
        <span className={styles.label}>
          {t('taskManagement.fields.description')}
          :
        </span>
        <span className={styles.value}>{data?.description || '-'}</span>
      </div>
      <div className={`${styles.infoItem} ${styles.infoItemUsers} ${styles.infoItemAttachments}`}>
        <div className={styles.infoItemLabel}>
          <span className={styles.label}>
            {t('taskManagement.fields.attachedFile')}
            :
          </span>
          <ButtonAddPlus
            onClick={() => onOpenCreateTaskModal(true)}
            hint={t('common.addNewFile')}
          />
        </div>
        <div className={styles.value}>
          {attachments.length > 0 ? (
            <div className={styles.attachments}>
              {attachments.map((attachment) => (
                <a
                  key={[attachment?.id, attachment?.fileUrl].join('_')}
                  href={formatFileUrl(attachment?.fileUrl || '')}
                  className={styles.attachmentItem}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FileUploadIcon className={styles.attachmentIcon} />
                  {getFileNameWithExtension(attachment?.fileName || '', attachment?.fileUrl || '')}
                </a>
              ))}
            </div>
          ) : (
            <div className={styles.emptyAttachments}>
              <EmptyList text={t('taskManagement.noFilesAttached')} />
            </div>
          )}
        </div>
      </div>

    </div>
  );
}

export default TaskInfoDetails;
